import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from '../../../components/Config/api.service';
import EmployeesPrime from './EmployeesPrime';
import { notify } from "../../../components/Utils/Notifications";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import Modal from '../../../components/Utils/ModalShow';
import AccountSelectField from '../../Settings/Tabs/Account/AccountSelectField';

const BasePrimeForm = ({ 
    id, 
    prime, 
    setPrime, 
    closeEdit, 
    title, 
    children, 
    onBeforeSave, 
    apiEndpoint = "/prime",
    type = "prime",
    isAdvanced = false, // Nouveau prop
    onAdvancedToggle = null // Nouveau prop
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [isEmployeeSectionExpanded, setIsEmployeeSectionExpanded] = useState(true);
    const [employeesPrimeChanges, setEmployeesPrimeChanges] = useState({
        employeesPrimes: [],
        newEmployeesPrimes: [],
        modifiedEmployeesPrimes: [],
        removedEmployeesPrimes: []
    });

    const handleEmployeesPrimesChange = useCallback((changes) => {
        setEmployeesPrimeChanges(changes);
    }, []);

    useEffect(() => {
        if (id) {
            setLoading(true);
            apiService.getElementById(apiEndpoint, id)
                .then(response => {
                    const dataKey = type === "advantage" ? "advantage" : "prime";
                    setPrime(response.data[dataKey]);
                })
                .catch(() => notify(t('basePrimeForm.fetchError'), '', 3))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, setPrime, apiEndpoint, type]);

    const saveEmployeePrimeChanges = async (primeId) => {
        const { newEmployeesPrimes, modifiedEmployeesPrimes, removedEmployeesPrimes } = employeesPrimeChanges;
        if (newEmployeesPrimes.length === 0 && modifiedEmployeesPrimes.length === 0 && removedEmployeesPrimes.length === 0) {
            return; // Rien à sauvegarder
        }
        try {
            const employeePrimeData = {
                newEmployees: newEmployeesPrimes,
                modifiedEmployees: modifiedEmployeesPrimes,
                removedEmployees: removedEmployeesPrimes
            };
            await apiService.saveElement(`${apiEndpoint}/${primeId}/employees`, employeePrimeData);
            notify(t('basePrimeForm.employeesUpdated'), '', 2);
        } catch (error) {
            notify(t('basePrimeForm.employeesUpdateError'), '', 3);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const primeToSave = { ...prime };
        if (onBeforeSave) {
            onBeforeSave(primeToSave);
        }

        try {
            if (id) {
                await apiService.updateElementById(apiEndpoint, id, primeToSave);
                await saveEmployeePrimeChanges(id);
                notify(t('basePrimeForm.updateSuccess'), '', 2);
            } else {
                const response = await apiService.saveElement(apiEndpoint, primeToSave);
                const responseKey = type === "advantage" ? "advantage" : "prime";
                const newPrimeId = response.data[responseKey];
                await saveEmployeePrimeChanges(newPrimeId);
                notify(t('basePrimeForm.createSuccess'), '', 2);
            }
            closeEdit();
        } catch (error) {
            notify(error.userMessage || t('basePrimeForm.genericError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const confirmArchive = async () => {
        try {
            await apiService.updateElementById(apiEndpoint, id, { active: false });
            notify(t('basePrimeForm.archiveSuccess'), '', 2);
            setIsArchiveModalOpen(false);
            closeEdit();
        } catch (error) {
            notify(t('basePrimeForm.archiveError'), '', 3);
            setIsArchiveModalOpen(false);
        }
    };

    const dropdownActions = [{
        label: t('basePrimeForm.archive'),
        onClick: () => setIsArchiveModalOpen(true),
        disabled: loading || !id,
        color: 'text-red-500',
        icon: <FiTrash2 size={16} />
    }];

    const loadAccountOptions = async (inputValue) => {
        if (inputValue.length < 2) return [];
        try {
            const response = await apiService.getElement(`/accounting/accounts/search?q=${inputValue}`);
            return response.data.map((account) => ({
                value: account.code,
                label: `${account.code} - ${account.name}`,
                account: account,
            }));
        } catch (error) {
            console.error("Erreur lors de la recherche des comptes:", error);
            return [];
        }
    };

    // Adapter le texte selon le type
    const entityName = type === "advantage" ? "advantage" : "prime";

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className='flex justify-between items-center mb-6'>
                    <h1 className="text-2xl font-bold flex items-center">
                        <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                        {title}
                    </h1>
                    <div>
                        <button type='button' className='bg-[--color-orange] text-white rounded-full btn mx-2' onClick={closeEdit}>{t('basePrimeForm.cancel')}</button>
                        <button type="submit" className='bg-[--color-primary] text-white hover:bg-[--bg-hover] rounded-full btn' disabled={loading}>
                            {loading ? <ClipLoader color='white' size={20} /> : t('basePrimeForm.save')}
                        </button>
                        <ActionDropdown actions={dropdownActions} />
                    </div>
                </div>

                {/* Section Informations générales */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.generalInfo')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{type === "advantage" ? t('basePrimeForm.basicDetailsAdvantage') : t('basePrimeForm.basicDetailsPrime')}</p>
                            </div>
                            {onAdvancedToggle && (
                                <div className="flex items-center gap-3">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={isAdvanced}
                                            onChange={e => onAdvancedToggle(e.target.checked)}
                                            disabled={!!id}
                                        />
                                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--color-primary]"></div>
                                    </label>
                                    <div className="text-sm">
                                        <span className="font-medium text-gray-700">{isAdvanced ? t('basePrimeForm.advancedMode') : t('basePrimeForm.simpleMode')}</span>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {isAdvanced ? t('basePrimeForm.advancedModeHelp') : t('basePrimeForm.simpleModeHelp')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-6">
                        {children}
                    </div>
                </div>

                {/* Section Gestion des employés */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div
                        className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setIsEmployeeSectionExpanded(!isEmployeeSectionExpanded)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.employeeManagement')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{t('basePrimeForm.employeeManagementSubtitle')}</p>
                            </div>
                            <div className="text-gray-400">
                                {isEmployeeSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>
                    </div>
                    {isEmployeeSectionExpanded && (
                        <div className="p-6 space-y-6">
                            <div className="flex items-center justify-between p-4 bg-[--bg-color] rounded-lg border border-[--border-color]">
                                <div>
                                    <h4 className="font-medium text-[--color-primary]">{t('basePrimeForm.applicationMode')}</h4>
                                    <p className="text-sm mt-1">{entityName === "advantage" ? t('basePrimeForm.applicationModeHelpAdvantage') : t('basePrimeForm.applicationModeHelpPrime')}</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <span className="mr-3 text-sm font-medium text-gray-700">
                                        {prime.apply_selected ? t('basePrimeForm.selection') : t('basePrimeForm.all')}
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="apply_selected"
                                        checked={!!prime.apply_selected}
                                        onChange={e => setPrime(p => ({ ...p, apply_selected: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--color-primary]"></div>
                                </label>
                            </div>
                            
                            <EmployeesPrime 
                                primeId={id} 
                                onEmployeesPrimesChange={handleEmployeesPrimesChange} 
                                saveChanges={() => saveEmployeePrimeChanges(id)}
                                apiEndpoint={apiEndpoint} // Passer l'endpoint
                                type={type} // Passer le type
                            />
                        </div>
                    )}
                </div>

                {/* Section Configuration comptable */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div
                        className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.accountingConfig')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{t('basePrimeForm.accountingConfigSubtitle')}</p>
                            </div>
                            <div className="text-gray-400">
                                {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>
                    </div>
                    {isAccountSectionExpanded && (
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <AccountSelectField
                                    label={t('basePrimeForm.debitAccount')}
                                    value={prime.account_debit}
                                    onChange={(option) => setPrime(p => ({ ...p, account_debit: option ? option.value : '' }))}
                                    placeholder={t('basePrimeForm.search')}
                                    loadAccountOptions={loadAccountOptions}
                                />
                                <AccountSelectField
                                    label={t('basePrimeForm.creditAccount')}
                                    value={prime.account_credit}
                                    onChange={(option) => setPrime(p => ({ ...p, account_credit: option ? option.value : '' }))}
                                    placeholder={t('basePrimeForm.search')}
                                    loadAccountOptions={loadAccountOptions}
                                />
                            </div>

                            <div className="border-t border-gray-200 pt-6">
                                <h4 className="text-md font-medium text-gray-800 mb-4">{t('basePrimeForm.calculationOptions')}</h4>
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <h5 className="font-medium text-gray-800">{t('basePrimeForm.notComputedInNet')}</h5>
                                        <p className="text-sm text-gray-600 mt-1">{entityName === "advantage" ? t('basePrimeForm.notComputedInNetHelpAdvantage') : t('basePrimeForm.notComputedInNetHelpPrime')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            name="not_computed_in_net"
                                            checked={!!prime.not_computed_in_net}
                                            onChange={e => setPrime(p => ({ ...p, not_computed_in_net: e.target.checked }))}
                                        />
                                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--color-primary]"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </form>

            <Modal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                title={t('basePrimeForm.archiveConfirmTitle')}
                footer={
                    <>
                        <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>{t('basePrimeForm.cancel')}</button>
                        <button className="btn btn-error" onClick={confirmArchive}>{t('basePrimeForm.confirm')}</button>
                    </>
                }
            >
                <p>{entityName === "advantage" ? t('basePrimeForm.archiveConfirmQuestionAdvantage') : t('basePrimeForm.archiveConfirmQuestionPrime')}</p>
            </Modal>
        </>
    );
};

export default BasePrimeForm;
